import React, { useEffect, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  // Tooltip,
  Typography, TableFooter, Pagination
} from '@mui/material';
import { palette } from '../../../assets/styles/themes/tokens';
import { routes } from '../../../constants';
import { buildAmpliSubscriptionProducts } from '../../../utils/schoolSeats';
import { useSelector } from 'react-redux';
import { selectUser, selectSchoolInvoiceHistory, selectSelectedSchoolId } from '../../../reducers/user.selectors';
import useRouter from '../../../utils/useRouter';
import { getSchoolInvoices, getSchoolSeats } from '../../../actions/user.actions';
import useAction from '../../../utils/useAction';
import { format } from 'date-fns';
import de from 'date-fns/locale/de';
import { subscriptionDateFormat } from '../../../constants';
import { currencyFormat } from '../../../utils/format';
import initFileDownload from '../../utils/downloadIFrame';
import schoolService from '../../../services/school.service';
import { SchoolInvoice, SchoolInvoiceDownloadLinkResponse } from '@kimuno/api-sdk/lib/types/responses/Teacher';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { SnackbarContext } from '../../../state/SnackbarContextProvider';
import services from '../../../services';
import { InvoiceDownloaded } from '../../../ampli';

const SchoolLicenseBillingPage: React.FC = () => {
  const { router } = useRouter();
  const user = useSelector(selectUser);
  const schoolInvoiceHistory = useSelector(selectSchoolInvoiceHistory);
  const getSchoolInvoicesAction = useAction(getSchoolInvoices);
  const [serverError, setServerError] = useState<Error | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const getSchoolSeatsAction = useAction(getSchoolSeats);
  const schoolSeats = useSelector((state: any) => state.user.schoolSeats);
  const { t } = useTranslation();
  const pageSize = 20;
  const { openSnackbar } = useContext(SnackbarContext);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);

  useEffect(() => {
    if (user && !user.isSchoolAdmin) {
      router.push(`/${routes.dashboard.root}`);
    }
  }, [user?.isSchoolAdmin]);

  useEffect(() => {
    if (user && user.isSchoolAdmin && selectedSchoolId) {
      getSchoolSeatsAction(selectedSchoolId);
    }
  }, [ user?.isSchoolAdmin, selectedSchoolId ]);

  useEffect(() => {
    if (user && user.isSchoolAdmin && selectedSchoolId) {
      try {
        getSchoolInvoicesAction(selectedSchoolId, currentPage, pageSize).then((data: any) => {
          if (data.type !== 'GET_SCHOOL_INVOICES_SUCCESS') {
            openSnackbar({
              type: 'error',
              message: t('Error while fetching invoices')
            });
          }
        });
      } catch (error: any) {
        openSnackbar({
          type: 'error',
          message: t('Error while fetching invoices')
        });
      }
    }
  }, [ user?.isSchoolAdmin, currentPage, selectedSchoolId ]);

  const onClickDownload = async (invoiceId: string, amount: number) => {
    if (!user || !selectedSchoolId) return;
    try {
      const invoiceLink: SchoolInvoiceDownloadLinkResponse =
          await schoolService.getSchoolInvoiceDownloadLink(selectedSchoolId, invoiceId);
      initFileDownload(invoiceLink.file);

      if (user.isSchoolAdmin && schoolSeats && selectedSchoolId && invoiceId) {
        const subscriptionProducts = buildAmpliSubscriptionProducts(schoolSeats);

        services.track.ampliEventTrack({
          event: new InvoiceDownloaded({
            'school id': selectedSchoolId,
            'amount': amount ?? 0,
            'invoice id': invoiceId,
            'subscription products': subscriptionProducts
          })
        });
      }
    } catch (error: any) {
      setServerError(error);
      openSnackbar({
        type: 'error',
        message: t(error.message)
      });
    }
  };

  const onPageIndexChange = (event: any, page: number) => {
    if (event && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const formatInvoiceDate = (invoiceDate: string) => {
    let newDate = '';
    try {
      newDate = format(
        new Date(invoiceDate),
        subscriptionDateFormat,
        { locale: de }
      );
    }
    catch {
      return '';
    }
    return newDate;
  };

  return (
    <Box className='root' pt={1.25} sx={{ minHeight: '100%', backgroundColor: 'rgb(249, 250, 251)' }}>
      <Box className='container'>
        <Typography
          variant='h5'
          sx={{ color: palette.text.primary }}
          marginBottom='10px'
          marginTop='10px'
        >
          {t('Billing History')}
        </Typography>
        {schoolInvoiceHistory?.invoices && schoolInvoiceHistory.invoices.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('Invoice Number')}</TableCell>
                  <TableCell>{t('Date')}</TableCell>
                  <TableCell>{t('Amount')}</TableCell>
                  <TableCell>{t('Status')}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schoolInvoiceHistory.invoices.map((invoice: SchoolInvoice, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>{formatInvoiceDate(invoice.invoiceDate)}</TableCell>
                    <TableCell>{currencyFormat(invoice.amount)}</TableCell>
                    <TableCell>
                      <Box
                        component='span'
                        color={invoice.status === 'Paid' ? '#388e3c' : '#d32f2f'}
                        fontWeight={500}
                        display='flex'
                        alignItems='center'
                        gap={0.5}
                      >
                        {invoice.status === 'Paid' ? (
                          <><CheckCircleIcon fontSize='small' />{t(invoice.status)}</>
                        ) : (
                          <><RemoveCircleOutline fontSize='small' />{t(invoice.status)}</>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        color='primary'
                        startIcon={<DownloadIcon fontSize='small' />}
                        onClick={() => onClickDownload(invoice.id, invoice.amount)}
                      >
                        {t('Download')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              { schoolInvoiceHistory.count > pageSize && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={5} align='center'>
                      <Pagination
                        count={(Math.ceil(schoolInvoiceHistory.count / pageSize)) || 1}
                        page={currentPage}
                        onChange={onPageIndexChange}
                        color='primary'
                        size='large'
                        showFirstButton
                        showLastButton
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        ) : (
          <Typography>{t('No invoices found')}</Typography>
        )}
        {serverError && (
          <Typography color='error' sx={{ mt: 2 }}>
            {t('Ups, something went wrong')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SchoolLicenseBillingPage;
